import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, getFirestore, query, setDoc, where } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { useCallback, useEffect, useState } from 'react';
import { app } from '../firebase';
import './ProfilePage.css';

const db = getFirestore(app);
const auth = getAuth(app);
const storage = getStorage(app);

const ProfilePage = ({ 
  onPlayTrack, 
  onAddToCart, 
  onAddToPlaylist, 
  playlists,
  currentTrack,
  isPlaying 
}) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState({
    name: '',
    bio: '',
    profilePicture: '',
    socialLinks: { twitter: '', instagram: '', facebook: '' },
  });
  const [tracks, setTracks] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activeTab, setActiveTab] = useState('tracks');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchProfile = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Fetch profile from Firestore
      const docRef = doc(db, 'profiles', user.uid);
      const docSnap = await getDoc(docRef);
      
      let profileData = {
        name: user.displayName || '',
        bio: '',
        profilePicture: user.photoURL || '',
        socialLinks: { twitter: '', instagram: '', facebook: '' },
      };
      let followersData = [];
      let followingData = [];
      let activityData = [];
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        profileData = data.profile || profileData;
        followersData = Array.isArray(data.followers) ? data.followers : [];
        followingData = Array.isArray(data.following) ? data.following : [];
        activityData = Array.isArray(data.recentActivity) ? data.recentActivity : [];
      }
      
      setProfile(profileData);
      setFollowers(followersData);
      setFollowing(followingData);
      setRecentActivity(activityData);

      // Fetch user's tracks from tracks collection
      const tracksQuery = query(collection(db, 'tracks'), where('userId', '==', user.uid));
      const tracksSnapshot = await getDocs(tracksQuery);
      const userTracks = tracksSnapshot.docs.map(doc => {
        const trackData = doc.data();
        return {
          id: doc.id,
          title: trackData.title || 'Untitled',
          artist: {
            id: user.uid,
            name: profileData.name || user.displayName || 'Unknown Artist',
            avatarUrl: profileData.profilePicture || user.photoURL || '',
            followers: followersData.length,
            verified: false
          },
          price: parseFloat(trackData.price) || 0,
          duration: '0:00',
          bpm: 0,
          genre: trackData.genre || 'Uncategorized',
          coverUrl: trackData.coverImage || '/default-track.jpg',
          audioUrl: trackData.fileUrl || '',
          plays: 0,
          likes: 0
        };
      });
      setTracks(userTracks);
      setError('');
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load profile.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Refresh profile when window regains focus (in case track was uploaded)
  useEffect(() => {
    const handleFocus = () => {
      if (user) {
        fetchProfile();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [user, fetchProfile]);

  const handleEditProfile = async (newProfile) => {
    if (!user) return;
    setSaving(true);
    setError('');
    try {
      // Get current tracks data
      const tracksQuery = query(collection(db, 'tracks'), where('userId', '==', user.uid));
      const tracksSnapshot = await getDocs(tracksQuery);
      const tracksData = tracksSnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title || 'Untitled',
        price: parseFloat(doc.data().price) || 0
      }));

      await setDoc(doc(db, 'profiles', user.uid), {
        profile: newProfile,
        tracks: tracksData,
        followers,
        following,
        recentActivity: [`Edited profile on ${new Date().toLocaleDateString()}`, ...recentActivity],
      });
      setProfile(newProfile);
      setRecentActivity(prev => [`Edited profile on ${new Date().toLocaleDateString()}`, ...prev]);
      setShowEdit(false);
      setError('');
      // Refresh profile to get updated data
      await fetchProfile();
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (file && user) {
      try {
        setSaving(true);
        setError('');
        const storageRef = ref(storage, `profilePictures/${user.uid}_${Date.now()}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        const updatedProfile = { ...profile, profilePicture: url };
        await handleEditProfile(updatedProfile);
        setError('');
      } catch (err) {
        console.error('Error uploading profile picture:', err);
        setError('Failed to upload profile picture: ' + err.message);
        setSaving(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-login-prompt">
          <h2>Please log in to view your profile</h2>
          <p>You need to be logged in to access your profile page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <EditProfileModal
        isOpen={showEdit}
        profile={profile}
        onSave={handleEditProfile}
        onClose={() => setShowEdit(false)}
        user={user}
      />

      {error && <div className="profile-error">{error}</div>}

      <div className="profile-header-section">
        <div className="profile-cover">
          <div className="profile-cover-overlay"></div>
        </div>
        <div className="profile-header-content">
          <div className="profile-avatar-container">
            {profile.profilePicture || user.photoURL ? (
              <img
                src={profile.profilePicture || user.photoURL}
                alt={profile.name || 'Profile'}
                className="profile-avatar"
              />
            ) : (
              <div className="profile-avatar-placeholder">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20.59 22C20.59 18.13 16.74 15 12 15C7.26 15 3.41 18.13 3.41 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
            <label className="profile-avatar-upload">
              <input
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                disabled={saving}
              />
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path d="M10 2V10M10 10L6 6M10 10L14 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </label>
          </div>
          <div className="profile-info-section">
            <div className="profile-main-info">
              <p className="profile-bio">{profile.bio || 'No bio yet. Click Edit Profile to add one.'}</p>
              
              <div className="profile-stats">
                <div className="profile-stat">
                  <span className="stat-value">{tracks.length}</span>
                  <span className="stat-label">TRACKS</span>
                </div>
                <div className="profile-stat">
                  <span className="stat-value">{followers.length}</span>
                  <span className="stat-label">FOLLOWERS</span>
                </div>
                <div className="profile-stat">
                  <span className="stat-value">{following.length}</span>
                  <span className="stat-label">FOLLOWING</span>
                </div>
              </div>

              <button className="edit-profile-button" onClick={() => setShowEdit(true)}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M11 3H5C3.89543 3 3 3.89543 3 5V15C3 16.1046 3.89543 17 5 17H15C16.1046 17 17 16.1046 17 15V9M14 2L17 5L10 12H7V9L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Edit Profile
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-tabs-section">
        <button
          className={`profile-tab ${activeTab === 'tracks' ? 'active' : ''}`}
          onClick={() => setActiveTab('tracks')}
        >
          Tracks ({tracks.length})
        </button>
        <button
          className={`profile-tab ${activeTab === 'followers' ? 'active' : ''}`}
          onClick={() => setActiveTab('followers')}
        >
          Followers ({followers.length})
        </button>
        <button
          className={`profile-tab ${activeTab === 'following' ? 'active' : ''}`}
          onClick={() => setActiveTab('following')}
        >
          Following ({following.length})
        </button>
        <button
          className={`profile-tab ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          Activity
        </button>
      </div>

      <div className="profile-content-section">
        {activeTab === 'tracks' && (
          <div className="profile-tracks">
            {tracks.length === 0 ? (
              <div className="empty-state">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18V5L21 3V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="18" cy="16" r="3" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <h3>No tracks yet</h3>
                <p>Upload your first track to get started!</p>
              </div>
            ) : (
              <div className="tracks-grid">
                {tracks.map(track => (
                  <div key={track.id} className="profile-track-card">
                    <div className="profile-track-cover" onClick={() => onPlayTrack(track)}>
                      <img src={track.coverUrl} alt={track.title} />
                      <div className="profile-track-overlay">
                        <button
                          className={`profile-play-button ${currentTrack?.id === track.id && isPlaying ? 'playing' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onPlayTrack(track);
                          }}
                        >
                          {currentTrack?.id === track.id && isPlaying ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect x="6" y="4" width="4" height="16" fill="currentColor"/>
                              <rect x="14" y="4" width="4" height="16" fill="currentColor"/>
                            </svg>
                          ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                            </svg>
                          )}
                        </button>
                      </div>
                      <div className="profile-track-genre">{track.genre}</div>
                    </div>
                    <div className="profile-track-info">
                      <h3>{track.title}</h3>
                      <p>{track.artist.name}</p>
                      <div className="profile-track-footer">
                        <span className="profile-track-price">
                          {track.price > 0 ? `$${track.price.toFixed(2)}` : 'Free'}
                        </span>
                        <div className="profile-track-actions">
                          <button
                            className="profile-action-button"
                            onClick={() => onAddToCart(track)}
                            title="Add to cart"
                          >
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                              <path d="M5 5V3C5 1.89543 5.89543 1 7 1H13C14.1046 1 15 1.89543 15 3V5M5 5H3C1.89543 5 1 5.89543 1 7V17C1 18.1046 1.89543 19 3 19H17C18.1046 19 19 18.1046 19 17V7C19 5.89543 18.1046 5 17 5H15M5 5H15" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'followers' && (
          <div className="profile-followers">
            {followers.length === 0 ? (
              <div className="empty-state">
                <h3>No followers yet</h3>
                <p>Share your profile to get followers!</p>
              </div>
            ) : (
              <div className="followers-list">
                {followers.map((follower, index) => (
                  <div key={index} className="follower-item">
                    <div className="follower-avatar">
                      {follower.charAt(0).toUpperCase()}
                    </div>
                    <div className="follower-name">{follower}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'following' && (
          <div className="profile-following">
            {following.length === 0 ? (
              <div className="empty-state">
                <h3>Not following anyone</h3>
                <p>Discover artists and start following!</p>
              </div>
            ) : (
              <div className="following-list">
                {following.map((followed, index) => (
                  <div key={index} className="following-item">
                    <div className="following-avatar">
                      {followed.charAt(0).toUpperCase()}
                    </div>
                    <div className="following-name">{followed}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="profile-activity">
            {recentActivity.length === 0 ? (
              <div className="empty-state">
                <h3>No activity yet</h3>
                <p>Your recent activity will appear here.</p>
              </div>
            ) : (
              <div className="activity-list">
                {recentActivity.map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className="activity-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2"/>
                        <path d="M10 6V10L13 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </div>
                    <div className="activity-content">{activity}</div>
                    <div className="activity-time">{new Date().toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const EditProfileModal = ({ isOpen, profile, onSave, onClose, user }) => {
  const [editProfile, setEditProfile] = useState(profile);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setEditProfile(profile);
      setProfilePicFile(null);
      setError('');
    }
  }, [isOpen, profile]);

  const isValidUrl = (url) => {
    if (!url || url.trim() === '') return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    
    if (!editProfile.name.trim()) {
      setError('Name is required.');
      setSaving(false);
      return;
    }

    if (!isValidUrl(editProfile.socialLinks.twitter) ||
        !isValidUrl(editProfile.socialLinks.instagram) ||
        !isValidUrl(editProfile.socialLinks.facebook)) {
      setError('Please enter valid URLs for social links.');
      setSaving(false);
      return;
    }

    let updatedProfile = { ...editProfile };
    
    if (profilePicFile && user) {
      try {
        const storageRef = ref(storage, `profilePictures/${user.uid}`);
        await uploadBytes(storageRef, profilePicFile);
        const url = await getDownloadURL(storageRef);
        updatedProfile.profilePicture = url;
      } catch (err) {
        setError('Failed to upload profile picture.');
        setSaving(false);
        return;
      }
    }

    try {
      await onSave(updatedProfile);
      onClose();
    } catch (err) {
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="edit-profile-modal-overlay" onClick={saving ? undefined : onClose} />
      <div className="edit-profile-modal">
        <div className="edit-profile-modal-header">
          <h2>Edit Profile</h2>
          <button className="edit-profile-close" onClick={onClose} disabled={saving}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="edit-profile-form">
          <div className="edit-profile-avatar-section">
            <img
              src={profilePicFile ? URL.createObjectURL(profilePicFile) : (editProfile.profilePicture || '/default-profile.jpg')}
              alt="Profile Preview"
              className="edit-profile-avatar-preview"
            />
            <label className="edit-profile-avatar-upload-btn">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setProfilePicFile(e.target.files[0])}
              />
              Change Photo
            </label>
          </div>

          <div className="edit-profile-form-group">
            <label htmlFor="edit-name">Name *</label>
            <input
              id="edit-name"
              type="text"
              value={editProfile.name}
              onChange={e => setEditProfile({ ...editProfile, name: e.target.value })}
              placeholder="Enter your name"
              required
            />
          </div>

          <div className="edit-profile-form-group">
            <label htmlFor="edit-bio">Bio</label>
            <textarea
              id="edit-bio"
              value={editProfile.bio}
              onChange={e => setEditProfile({ ...editProfile, bio: e.target.value })}
              placeholder="Tell us about yourself"
              rows="4"
            />
          </div>

          <div className="edit-profile-form-group">
            <label htmlFor="edit-twitter">Twitter URL</label>
            <input
              id="edit-twitter"
              type="text"
              placeholder="https://twitter.com/username"
              value={editProfile.socialLinks.twitter}
              onChange={e => setEditProfile({
                ...editProfile,
                socialLinks: { ...editProfile.socialLinks, twitter: e.target.value }
              })}
            />
          </div>

          <div className="edit-profile-form-group">
            <label htmlFor="edit-instagram">Instagram URL</label>
            <input
              id="edit-instagram"
              type="text"
              placeholder="https://instagram.com/username"
              value={editProfile.socialLinks.instagram}
              onChange={e => setEditProfile({
                ...editProfile,
                socialLinks: { ...editProfile.socialLinks, instagram: e.target.value }
              })}
            />
          </div>

          <div className="edit-profile-form-group">
            <label htmlFor="edit-facebook">Facebook URL</label>
            <input
              id="edit-facebook"
              type="text"
              placeholder="https://facebook.com/username"
              value={editProfile.socialLinks.facebook}
              onChange={e => setEditProfile({
                ...editProfile,
                socialLinks: { ...editProfile.socialLinks, facebook: e.target.value }
              })}
            />
          </div>

          {error && <div className="edit-profile-error">{error}</div>}

          <div className="edit-profile-actions">
            <button
              className="edit-profile-save-btn"
              onClick={handleSave}
              disabled={saving || !editProfile.name.trim()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              className="edit-profile-cancel-btn"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
